import type {MessageAttachment, MessageAttachmentKind} from '@microsoft/agent-host-protocol'
import type {ComposerAttachmentIcon} from '@github-ui/agent-sessions-interface/components/chat-composer-attachments'
import {tagPath} from '@github-ui/paths'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

// The protocol models the attachment discriminant as a `const enum`, which we
// can't import as a value under isolatedModules; the literal is its value
// (matches the pattern in repo-context.ts / image-context.ts).
const SIMPLE_ATTACHMENT = 'simple' as MessageAttachmentKind.Simple

// Per-attachment `_meta` key the Copilot host reads to losslessly rebuild the
// SDK attachment on `sendMessage` (spec: copilot/message-attachment.md).
const ATTACHMENT_DETAIL_META_KEY = 'copilot.attachmentDetail'

// Per-attachment `_meta` key for GitHub-UI-only presentation state that must NOT
// leak into the SDK attachment detail. The Copilot host reconstructs the SDK
// attachment verbatim from `ATTACHMENT_DETAIL_META_KEY`, so any field the SDK
// contract doesn't define (e.g. a PR's `draft` flag) lives here instead and is
// consumed only by our own icon selection.
const ATTACHMENT_UI_META_KEY = 'github.attachmentUi'

interface RepoRef {
  id: number
  name: string
  owner: string
  visibility?: string
}

/**
 * One side (base or head) of a {@link ImplicitReference} `file-diff`, mirroring
 * the fields of immersive's `FileReference`. Only the subset needed to build the
 * SDK's `AttachmentGitHubFileDiffSide` ({@link path}/{@link ref}/repo) is modelled.
 */
interface ImplicitFileDiffSide {
  path: string
  ref: string
  commitOID: string
  repoID: number
  repoOwner: string
  repoName: string
}

/**
 * The `CopilotChatReference` shapes returned by the dotcom implicit-context
 * endpoint (`GET /github-copilot/chat/implicit-context/:owner/:repo/:url`,
 * served by `Copilot::Chat::ContextController#show`). We consume this contract
 * as-is and translate the self-contained variants into AHP attachments — the
 * endpoint is the authoritative, route-recognized page resolver, so we avoid
 * re-parsing `window.location` on the client.
 *
 * Only the variants we can translate into a valid AHP attachment are modelled
 * here. Alert kinds (`code-scanning-alert`, `secret-scanning-alert`,
 * `dependabot-alert`) have no SDK attachment kind, and `tree-comparison` /
 * repository-less list kinds don't carry enough data (NWO / run id) to build a
 * valid pointer, so they are intentionally omitted from the MVP.
 */
export type ImplicitReference =
  | {
      type: 'repository'
      id: number
      name: string
      ownerLogin: string
      visibility?: string
      /** Directory path when on a tree page; empty on the repo root. Unused by the repo attachment. */
      path?: string
    }
  | {
      // `file-v2` is the newer discriminant for the same file payload
      // (`FileReference` in copilot-chat-types); both are renderable and map to
      // the same attachment.
      type: 'file' | 'file-v2'
      url: string
      path: string
      repoID: number
      repoOwner: string
      repoName: string
      ref: string
      commitOID: string
    }
  | {
      type: 'snippet'
      url: string
      path: string
      repoID: number
      repoOwner: string
      repoName: string
      ref: string
      commitOID: string
      range: {start: number; end: number}
    }
  | {
      // A single-file diff (a PR "Files changed" row, a commit-scoped file, or a
      // compare view), mirroring immersive's `FileDiffReference`. Maps to the
      // SDK's `AttachmentGitHubFileDiff`, which requires `url` and at least one
      // of `base`/`head`: `head` is null for a removed file, `base` for an added
      // file, so both sides are nullable here.
      type: 'file-diff'
      url: string
      baseFile: ImplicitFileDiffSide | null
      headFile: ImplicitFileDiffSide | null
    }
  | {type: 'commit'; oid: string; message?: string; permalink?: string; repository: RepoRef}
  | {type: 'release'; name?: string; tagName?: string; url?: string; repository: RepoRef}
  | {type: 'issue'; number: number; url: string; title?: string; state?: string; repository: RepoRef}
  | {type: 'discussion'; number: number; url: string; title?: string; state?: string; repository: RepoRef}
  | {
      type: 'pull-request'
      number: number
      /**
       * Absolute (or origin-relative) URL from the endpoint. Consumed verbatim
       * rather than reconstructed: the endpoint's `PullRequestReference.repository`
       * is a `CopilotChatRepo` (owner/name), with no `nwo` to build a path from.
       */
      url: string
      title?: string
      state?: string
      draft?: boolean
      repository: {id: number; name: string; ownerLogin: string; visibility?: string}
    }
  | {
      // An Actions job page (`/:owner/:repo/actions/runs/:run_id/job/:job_id`),
      // resolved and enriched by `Copilot::Chat::ContextController#job_props`.
      // `id` is the CheckRun id (the `:job_id` route segment); `workflow.id` is
      // the workflow *run* id (used to rebuild the job URL, matching immersive's
      // `JobReference`). jobName/workflow.name/jobUrl are optional here because
      // the type mirrors the immersive `JobReference` contract, but the endpoint
      // populates them from a server-side lookup.
      type: 'job'
      id: number
      repoId: number
      repoOwner: string
      repoName: string
      workflow?: {id: number; name?: string; filePath?: string}
      jobName?: string | null
      jobUrl?: string | null
      jobFailed?: boolean
    }

export type ReferenceType = 'issue' | 'pr' | 'discussion'

/** Resolves an origin-relative dotcom path to an absolute URL (SSR-safe). */
function absoluteUrl(pathOrUrl: string | undefined): string | undefined {
  if (!pathOrUrl) return undefined
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl
  // Derive the origin from the SSR-safe location (set by the server on GHES/
  // proxima tenants) rather than a hardcoded github.com, so links point at the
  // current host even during SSR. Matches repo-picker's `repositoryURL`.
  const origin = ssrSafeLocation.origin || 'https://github.com/'
  return `${origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`
}

/** Last path segment, matching copilot-chat's file/snippet token naming. */
function basename(path: string): string {
  return path.split('/').pop() || path
}

/**
 * Builds one side of the SDK's `AttachmentGitHubFileDiff` from an
 * {@link ImplicitFileDiffSide}. Pins the resolved SHA (falling back to the ref)
 * so the diff keeps meaning the same content after the branch advances, matching
 * the `file`/`snippet` handling above.
 */
function fileDiffSide(side: ImplicitFileDiffSide) {
  return {
    path: side.path,
    ref: side.commitOID || side.ref,
    repo: {owner: side.repoOwner, name: side.repoName, id: side.repoID},
  }
}

function simpleAttachment(
  label: string,
  displayKind: string,
  detail: Record<string, unknown>,
  uiMeta?: Record<string, unknown>,
): MessageAttachment {
  return {
    type: SIMPLE_ATTACHMENT,
    label,
    displayKind,
    _meta: {
      [ATTACHMENT_DETAIL_META_KEY]: detail,
      ...(uiMeta ? {[ATTACHMENT_UI_META_KEY]: uiMeta} : {}),
    },
  }
}

export function githubReference(
  referenceType: ReferenceType,
  number: number,
  url: string | undefined,
  title?: string,
  state?: string,
  draft?: boolean,
  privateRepository?: boolean,
): MessageAttachment | undefined {
  const absUrl = absoluteUrl(url)
  // The SDK's `AttachmentGitHubReference` requires `title`, `state`, and `url`,
  // and the host reconstructs this detail verbatim. During the backwards-
  // compatible rollout the endpoint may omit `title`/`state`, so fill them with
  // explicit fallbacks. `url` cannot be faked, so skip the attachment entirely
  // when we can't build a valid one rather than emit a detail the host rejects.
  if (!absUrl) return undefined
  const label = title ?? `#${number}`
  // `draft` is not part of the SDK's `AttachmentGitHubReference` contract, so it
  // rides UI-only meta (for icon selection) instead of the forwarded detail.
  return simpleAttachment(
    label,
    'github-reference',
    {
      type: 'github_reference',
      number,
      referenceType,
      title: label,
      state: state ?? '',
      url: absUrl,
    },
    draft !== undefined || privateRepository !== undefined
      ? {
          ...(draft !== undefined ? {draft} : {}),
          ...(privateRepository !== undefined ? {private: privateRepository} : {}),
        }
      : undefined,
  )
}

/**
 * Translates one implicit reference from the dotcom endpoint into the AHP
 * `MessageAttachment` the Copilot host reconstructs into the matching SDK
 * `Attachment` (spec: copilot/message-attachment.md). GitHub-anchored kinds ride
 * a `Simple` shell carrying the verbatim SDK blob under
 * `copilot.attachmentDetail`. Returns `undefined` for references we don't map so
 * callers can flat-map a mixed list safely.
 */
export function implicitReferenceToAttachment(ref: ImplicitReference): MessageAttachment | undefined {
  switch (ref.type) {
    case 'repository':
      return simpleAttachment(
        `${ref.ownerLogin}/${ref.name}`,
        'repository',
        {
          type: 'github_repository',
          repo: {owner: ref.ownerLogin, name: ref.name, id: ref.id},
          url: absoluteUrl(`/${ref.ownerLogin}/${ref.name}`),
        },
        ref.visibility ? {private: ref.visibility !== 'public'} : undefined,
      )

    case 'file':
    case 'file-v2':
      return simpleAttachment(basename(ref.path), 'github-file', {
        type: 'github_file',
        repo: {owner: ref.repoOwner, name: ref.repoName, id: ref.repoID},
        path: ref.path,
        // Pin the resolved SHA so the reference keeps meaning the same content
        // even after the branch advances.
        ref: ref.commitOID || ref.ref,
        url: absoluteUrl(ref.url),
      })

    case 'snippet':
      return simpleAttachment(`${basename(ref.path)}:${ref.range.start}-${ref.range.end}`, 'github-snippet', {
        type: 'github_snippet',
        repo: {owner: ref.repoOwner, name: ref.repoName, id: ref.repoID},
        path: ref.path,
        ref: ref.commitOID || ref.ref,
        url: absoluteUrl(ref.url),
        // The SDK's `AttachmentGitHubSnippet` names this field `lineRange`
        // (`AttachmentFileLineRange = {start, end}`); the host reconstructs the
        // detail verbatim, so the property name must match exactly.
        lineRange: ref.range,
      })

    case 'file-diff': {
      const url = absoluteUrl(ref.url)
      // Prefer the head (post-change) file, falling back to the base file for a
      // removed file. A truthy `labelSide` also means at least one side exists.
      const labelSide = ref.headFile ?? ref.baseFile
      // The SDK's `AttachmentGitHubFileDiff` requires `url` and at least one of
      // `base`/`head`; skip the attachment when we can't build a valid one
      // rather than emit a detail the host rejects.
      if (!url || !labelSide) return undefined
      const base = ref.baseFile ? fileDiffSide(ref.baseFile) : undefined
      const head = ref.headFile ? fileDiffSide(ref.headFile) : undefined
      return simpleAttachment(basename(labelSide.path), 'github-file-diff', {
        type: 'github_file_diff',
        url,
        ...(base ? {base} : {}),
        ...(head ? {head} : {}),
      })
    }

    case 'commit': {
      // Use `||` (not `??`) so an empty first line (empty commit messages are
      // valid) falls back to the OID rather than producing an unnamed token.
      const firstLine = ref.message?.split('\n')[0] ?? ''
      return simpleAttachment(firstLine || ref.oid, 'github-commit', {
        type: 'github_commit',
        oid: ref.oid,
        // The SDK's `AttachmentGitHubCommit.message` is the *first line* of the
        // commit message (not the full multi-line body), so forward only that —
        // the same value used to derive the label. Always include it (falling
        // back to the empty string) since the SDK requires `message: string`.
        message: firstLine,
        repo: {owner: ref.repository.owner, name: ref.repository.name, id: ref.repository.id},
        url: ref.permalink ?? absoluteUrl(`/${ref.repository.owner}/${ref.repository.name}/commit/${ref.oid}`),
      })
    }

    case 'release': {
      // The SDK's `AttachmentGitHubRelease` requires `tagName: string` — the
      // release is anchored to a git tag and the host cannot reconstruct the
      // attachment without one. The endpoint's `ReleaseReference` has optional
      // name/tagName/url, so skip releases with no tag rather than emit an
      // attachment the host will reject. Derive the label from name → tag (a
      // release can have an empty name but a valid tag, so use a truthy
      // fallback), and prefer the supplied URL, falling back to the tag path.
      if (!ref.tagName) return undefined
      const label = ref.name || ref.tagName
      const repo = {owner: ref.repository.owner, name: ref.repository.name, id: ref.repository.id}
      const url = ref.url
        ? absoluteUrl(ref.url)
        : absoluteUrl(tagPath({owner: ref.repository.owner, repo: ref.repository.name, tag: ref.tagName}))
      return simpleAttachment(label, 'github-release', {
        type: 'github_release',
        name: label,
        tagName: ref.tagName,
        repo,
        url,
      })
    }

    case 'issue':
      return githubReference('issue', ref.number, ref.url, ref.title, ref.state)

    case 'discussion':
      return githubReference('discussion', ref.number, ref.url, ref.title, ref.state)

    case 'pull-request':
      return githubReference(
        'pr',
        ref.number,
        ref.url,
        ref.title,
        ref.state,
        ref.draft,
        ref.repository.visibility ? ref.repository.visibility !== 'public' : undefined,
      )

    case 'job': {
      const repo = {owner: ref.repoOwner, name: ref.repoName, id: ref.repoId}
      // Prefer the endpoint's job URL; fall back to rebuilding it from the
      // workflow run id (as immersive's `jobRefUrl` does). The SDK's
      // `AttachmentGitHubActionsJob` requires `url`, so skip the attachment when
      // neither is available rather than emit a detail the host rejects.
      const url =
        absoluteUrl(ref.jobUrl ?? undefined) ??
        (ref.workflow?.id != null
          ? absoluteUrl(`/${ref.repoOwner}/${ref.repoName}/actions/runs/${ref.workflow.id}/job/${ref.id}`)
          : undefined)
      if (!url) return undefined
      // `jobName` and `workflowName` are required strings on the SDK blob; the
      // endpoint always supplies them, but fall back defensively (mirroring
      // immersive's `jobRefName`) so the host never receives a malformed detail.
      const jobName = ref.jobName || `${ref.repoOwner}/${ref.repoName} workflow job`
      return simpleAttachment(jobName, 'github-actions-job', {
        type: 'github_actions_job',
        jobId: ref.id,
        jobName,
        repo,
        url,
        workflowName: ref.workflow?.name ?? '',
        // The endpoint reports only pass/fail via `jobFailed`; surface a failure
        // as the SDK's optional `conclusion` when known. A non-failed job may be
        // success, cancelled, or still running, so leave `conclusion` unset then.
        ...(ref.jobFailed ? {conclusion: 'failure'} : {}),
      })
    }
  }
}

/** Maps a mixed list of references to attachments, dropping unmapped ones. */
export function implicitReferencesToAttachments(refs: ImplicitReference[]): MessageAttachment[] {
  return refs
    .map(implicitReferenceToAttachment)
    .filter((attachment): attachment is MessageAttachment => attachment !== undefined)
}

/**
 * Builds a `github_url` attachment for the current page. Unlike the entity kinds
 * above, this is never returned by the dotcom endpoint (which only resolves
 * repository-scoped entities): it is synthesized client-side as the generic
 * "we're on this GitHub page but it isn't a resolvable entity" anchor — the
 * embedded equivalent of copilot-chat's `addUrlAsImplicitReference`. It covers
 * both non-repository pages (which never reach the endpoint) and repository
 * pages the endpoint can't resolve to a renderable reference. The SDK's
 * `AttachmentGitHubUrl` detail is just `{type, url}` (spec:
 * copilot/message-attachment.md); the page title, when known, rides the `Simple`
 * shell's `label` for a friendlier chip but is intentionally kept out of the
 * verbatim detail the host reconstructs.
 */
export function githubUrlAttachment(url: string, title?: string): MessageAttachment {
  const absUrl = absoluteUrl(url) ?? url
  return simpleAttachment(title || absUrl, 'github-url', {
    type: 'github_url',
    url: absUrl,
  })
}

/**
 * Chooses the composer token icon for an implicit attachment. Issue and
 * pull-request tokens surface their state (mirroring the status glyphs used in
 * the rest of Copilot Chat) by reading the `github_reference` detail; the
 * remaining kinds map to their type glyph (repo/commit/release/snippet/file).
 * Missing/unknown state falls back to the neutral `issue`/`pull` glyph (muted,
 * not the green "open" tint) — important for the backwards-compatible rollout
 * where the endpoint may omit `state`.
 */
export function implicitAttachmentIcon(attachment: MessageAttachment): ComposerAttachmentIcon {
  switch (attachment.displayKind) {
    case 'repository':
      return 'repo'
    case 'github-commit':
      return 'commit'
    case 'github-release':
      return 'release'
    case 'github-snippet':
      return 'snippet'
    case 'github-file':
      return 'file'
    case 'github-file-diff':
      return 'diff'
    case 'github-url':
      return 'link'
    case 'github-actions-job':
      return 'workflow'
  }

  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as
    | {type?: string; referenceType?: string; state?: string}
    | undefined
  const uiMeta = attachment._meta?.[ATTACHMENT_UI_META_KEY] as {draft?: boolean} | undefined

  if (detail?.type === 'github_reference') {
    const state = detail.state
    if (detail.referenceType === 'pr') {
      if (uiMeta?.draft && (state === 'open' || !state)) return 'pull-draft'
      switch (state) {
        case 'open':
          return 'pull-open'
        case 'closed':
          return 'pull-closed'
        case 'merged':
          return 'pull-merged'
        default:
          return 'pull'
      }
    }
    if (detail.referenceType === 'discussion') return 'discussion'
    if (detail.referenceType === 'issue') {
      switch (state) {
        case 'open':
          return 'issue-open'
        case 'closed':
          return 'issue-closed'
        case 'not_planned':
          return 'issue-not-planned'
        default:
          return 'issue'
      }
    }
  }

  return 'file'
}

/** Absolute URL an implicit attachment points at, so its token can be a link. */
export function implicitAttachmentUrl(attachment: MessageAttachment): string | undefined {
  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as {url?: string} | undefined
  return typeof detail?.url === 'string' ? detail.url : undefined
}

/**
 * Stable identity for an implicit attachment, used for both chip dedup and
 * dismissal tracking. Snippet URLs embed the selected line range, which churns
 * as the user moves the Code View selection and can diverge from the displayed
 * chip during the resolve debounce window. Keying snippets by repo+ref+path
 * (omitting only the line range) means dismissing the visible snippet chip also
 * suppresses a freshly-resolved snippet for the same file at the same commit —
 * without it, removing the chip wouldn't stop a re-ranged snippet the user never
 * saw from being submitted — while still keeping snippets at the same path on a
 * different branch/commit distinct. Other kinds carry a stable URL, so we key on
 * that (falling back to display kind + label when absent).
 */
export function implicitAttachmentKey(attachment: MessageAttachment): string {
  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as
    | {type?: string; path?: string; ref?: string; repo?: {owner?: string; name?: string}}
    | undefined
  if (detail?.type === 'github_snippet' && detail.path) {
    return `github_snippet:${detail.repo?.owner ?? ''}/${detail.repo?.name ?? ''}:${detail.ref ?? ''}:${detail.path}`
  }
  const url = implicitAttachmentUrl(attachment)
  if (url) return url
  return `${attachment.displayKind ?? ''}:${attachment.label ?? ''}`
}
